import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Wallet,
  CreditCard,
  Target,
  RefreshCw,
  BarChart3,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  PieChart,
  Instagram,
} from "lucide-react";
import { PrefetchLink, useIdlePrefetch } from "@/components/PrefetchLink";
import { Seo, SITE_URL } from "@/components/Seo";
import { InstallAppButton } from "@/components/InstallAppButton";
import { Smartphone, Share, Plus, Download } from "lucide-react";


/* ---------- Reveal on scroll ---------- */
const Reveal = ({
  children,
  delay = 0,
  className = "",
  immediate = false,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  immediate?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(immediate);

  useEffect(() => {
    if (immediate) return;
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [immediate]);

  if (immediate) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transform-gpu transition-all duration-700 ease-out ${
        shown ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      } ${className}`}
    >
      {children}
    </div>
  );
};

/* ---------- Data ---------- */
const features = [
  {
    icon: CreditCard,
    emoji: "💳",
    title: "Cartões com fatura real",
    desc: "Fechamento e vencimento como no banco, faturas por mês, pagamento parcial e histórico compacto — sem surpresas.",
  },
  {
    icon: Target,
    emoji: "🎯",
    title: "Contas a pagar e a receber",
    desc: "Contas fixas recorrentes, cobranças de clientes com Pix ou boleto, alertas de vencimento e recebimento lançado no saldo.",
  },
  {
    icon: RefreshCw,
    emoji: "⚡",
    title: "Lançamentos em segundos",
    desc: "Receitas e despesas com categoria, cartão, banco e método de pagamento — dinheiro, PIX, débito, crédito e boleto.",
  },
  {
    icon: BarChart3,
    emoji: "📈",
    title: "Investimentos com aportes",
    desc: "Acompanhe rendimento e aportes mensais separadamente, com evolução do patrimônio e resumo consolidado da carteira.",
  },
  {
    icon: PieChart,
    emoji: "📊",
    title: "Relatórios para auditoria",
    desc: "Exporte CSV e PDF profissional com cabeçalho institucional, KPIs e detalhamento — pronto para contador e conciliação.",
  },
  {
    icon: ShieldCheck,
    emoji: "🔒",
    title: "Seguro e privado",
    desc: "Seus dados isolados por usuário, criptografia ponta a ponta e acesso apenas com sua conta. Nada é compartilhado.",
  },
];

const faqGroups = [
  {
    title: "Cartão de crédito e fatura",
    items: [
      {
        q: "Como funciona o controle de fatura real de cartão de crédito?",
        a: "O Meu Controle é um app de controle financeiro pessoal que replica o funcionamento do banco: você cadastra o dia de fechamento e o dia de vencimento de cada cartão de crédito e o sistema organiza as compras em faturas mensais reais. Compras feitas depois do fechamento entram automaticamente na próxima fatura, e pagamentos parciais antes do fechamento abatem o valor da fatura em aberto, igual ao extrato do seu banco.",
      },
      {
        q: "O app organiza compras parceladas no cartão?",
        a: "Sim. Ao lançar uma compra parcelada no cartão de crédito, cada parcela é distribuída automaticamente nas faturas mensais seguintes, respeitando o ciclo de fechamento e vencimento. Assim você enxerga o impacto real das parcelas no seu orçamento mês a mês.",
      },
    ],
  },
  {
    title: "Contas a pagar, orçamento e alertas",
    items: [
      {
        q: "Como funcionam os alertas de vencimento de contas a pagar?",
        a: "Na aba Orçamentos você cadastra contas a pagar (aluguel, energia, internet, assinaturas) com data de vencimento. O app de controle financeiro exibe alertas visuais quando a conta vence em até 3 dias (amarelo) ou já está em atraso (vermelho), e ainda mostra a previsão de gasto do mês. Contas fixas se repetem automaticamente todo mês, sem retrabalho.",
      },
      {
        q: "Consigo planejar orçamento mensal por categoria?",
        a: "Sim. Você define um orçamento mensal por categoria de gastos (alimentação, transporte, lazer, etc.) e o Sugestor Inteligente propõe valores com base na sua média real dos últimos 3 meses, com opção de desafio de economia de 10%.",
      },
      {
        q: "Como funciona o controle de contas a pagar e receber no app?",
        a: "Contas a pagar ficam em Orçamentos, com alertas de vencimento. Em Contas a Receber você cadastra cobranças de clientes com nome, CPF/CNPJ opcional, descrição, vencimento, valor e forma de cobrança (Boleto, Cartão, Dinheiro, Pix ou Transferência). Cobranças vencendo hoje ou atrasadas ficam em vermelho e, ao confirmar o recebimento, a entrada é lançada automaticamente no seu saldo.",
      },
      {
        q: "Como ter controle financeiro pessoal sem usar planilha?",
        a: "Com um aplicativo de controle financeiro pessoal online como o Meu Controle, você registra gastos em segundos pelo celular, vê os saldos por banco e cartão atualizados na hora e recebe alertas de vencimento — sem fórmulas, sem arquivos perdidos e com relatórios em PDF e Excel.",
      },
    ],
  },
  {
    title: "Assinatura, teste grátis e pagamento",
    items: [
      {
        q: "Como pago a assinatura do Meu Controle pela página?",
        a: "Após criar sua conta gratuita, acesse a aba Assinatura pelo menu, escolha o plano mensal ou anual e finalize o pagamento com PIX ou cartão de crédito via checkout seguro do Mercado Pago. A liberação do acesso premium é automática assim que o pagamento é aprovado.",
      },
      {
        q: "Existe teste grátis? Preciso cadastrar cartão de crédito?",
        a: "Sim. O Meu Controle oferece período de teste gratuito, sem precisar cadastrar cartão de crédito. É só criar a conta com e-mail e senha e começar a usar todas as funcionalidades de controle financeiro imediatamente.",
      },
      {
        q: "Posso cancelar a assinatura a qualquer momento?",
        a: "Sim, sem fidelidade e sem multa. O cancelamento da assinatura é feito direto na aba Assinatura e você mantém o acesso premium até o fim do período já pago.",
      },
    ],
  },
  {
    title: "Segurança, PWA e exportação de dados",
    items: [
      {
        q: "Meus dados financeiros pessoais ficam seguros?",
        a: "Sim. Os dados financeiros são criptografados e isolados por usuário no banco de dados com Row Level Security — cada conta só enxerga as próprias informações. Nada é compartilhado com terceiros nem usado para publicidade, e não pedimos senha do seu banco.",
      },
      {
        q: "Posso instalar o Meu Controle como aplicativo no celular?",
        a: "Sim. O Meu Controle é um PWA (Progressive Web App) de finanças pessoais: funciona no celular Android, iPhone, tablet e desktop e pode ser instalado direto pelo navegador, sem passar por loja de aplicativos.",
      },
      {
        q: "Consigo exportar meus lançamentos em PDF ou CSV para o contador?",
        a: "Sim. Na aba Relatórios você exporta os lançamentos em CSV para planilhas (Excel, Google Sheets) ou em PDF profissional com cabeçalho institucional, KPIs, categorias e detalhamento por transação — pronto para auditoria, imposto de renda e conciliação com o contador.",
      },
    ],
  },
];

const faqs = faqGroups.flatMap((g) => g.items);

const landingJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const Landing = () => {
  useIdlePrefetch(["/blog", "/auth"]);

  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div
      className="min-h-screen bg-[hsl(220_45%_98%)] text-[hsl(222_47%_11%)] antialiased"
      style={{ fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif" }}
    >
      <Seo
        title="Meu Controle | App de Controle Financeiro Pessoal (Teste Grátis)"
        description="App de controle financeiro pessoal: faturas de cartão, contas a pagar e a receber, orçamentos e investimentos. Teste grátis por 5 dias, sem cartão!"
        imageAlt="Meu Controle — app de controle financeiro pessoal"
        path="/"
        jsonLd={landingJsonLd}
      />


      {/* ---------- Header ---------- */}
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0a1f44] to-[#10b981] shadow-md shadow-[#10b981]/20">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <span className="flex flex-col leading-none">
              <span className="text-base font-bold tracking-tight text-[#0a1f44]">
                Meu Controle
              </span>
              <span className="hidden text-[10px] font-semibold uppercase tracking-[0.14em] text-[#047857] sm:block">
                Rumo Financeiro
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 text-sm font-medium text-slate-600 md:flex">
            <a href="#funcionalidades" className="transition hover:text-[#0a1f44]">
              Funcionalidades
            </a>
            <a href="#solucao" className="transition hover:text-[#0a1f44]">
              Por que usar
            </a>
            <a href="#faq" className="transition hover:text-[#0a1f44]">
              FAQ
            </a>
            <PrefetchLink to="/blog" className="transition hover:text-[#0a1f44]">
              Blog
            </PrefetchLink>
          </nav>

          <div className="flex items-center gap-2">
            <InstallAppButton variant="header" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/auth")}
              className="text-slate-700 hover:bg-slate-100 hover:text-[#0a1f44]"
            >
              Entrar
            </Button>
            <Button
              size="sm"
              onClick={() => navigate("/auth")}
              className="bg-[#047857] text-white shadow-md shadow-[#047857]/30 hover:bg-[#065f46]"
            >
              Começar agora
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* ---------- Hero ---------- */}
        <section className="relative overflow-hidden">
          {/* Background decoration */}
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#0a1f44]/10 blur-3xl" />
            <div className="absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#10b981]/15 blur-3xl" />
          </div>

          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-2 lg:pt-24">
            <Reveal immediate>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#047857]/30 bg-[#047857]/10 px-3 py-1 text-xs font-semibold text-[#065f46]">
                <Sparkles className="h-3.5 w-3.5" />
                Teste grátis · Sem cartão de crédito
              </div>

              <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-[#0a1f44] sm:text-5xl lg:text-6xl">
                App de controle financeiro pessoal: cartões, contas e investimentos
              </h1>
              <p className="subtitle mt-2 text-2xl font-bold text-slate-600 sm:text-3xl">
                Assuma o controle definitivo do seu dinheiro.
              </p>


              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                Transações, faturas de cartão, contas a pagar e a receber, orçamentos e
                investimentos em um só lugar. Simples, rápido e mais prático que planilha.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  size="lg"
                  onClick={() => navigate("/auth")}
                  className="group h-12 gap-2 bg-[#047857] px-7 text-base font-semibold text-white shadow-xl shadow-[#047857]/30 transition hover:-translate-y-0.5 hover:bg-[#065f46] hover:shadow-[#047857]/50"
                >
                  Começar agora — é grátis
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <InstallAppButton variant="hero" />
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <ShieldCheck className="h-4 w-4 text-[#10b981]" />
                  Dados criptografados ponta a ponta
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-slate-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#10b981]" /> Sem fidelidade
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#10b981]" /> Cancele quando quiser
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#10b981]" /> 100% em português
                </span>
              </div>
            </Reveal>

            {/* Mockup */}
            <Reveal immediate>
              <div className="relative mx-auto w-full max-w-lg">
                <div className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-[#0a1f44]/20 via-[#10b981]/20 to-transparent blur-2xl" />
                <picture>
                  <source srcSet="/dashboard-mockup.webp" type="image/webp" />
                  <img
                    src="/dashboard-mockup.jpg"
                    alt="Painel do app Meu Controle exibindo saldo atual e gráfico de gastos por categoria"
                    width={1024}
                    height={1024}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    sizes="(max-width: 640px) 100vw, 512px"
                    className="aspect-square h-auto w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10"
                  />
                </picture>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ---------- Funcionalidades ---------- */}
        <section id="funcionalidades" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#047857]">
                O que o app faz
              </span>
              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a1f44] sm:text-4xl">
                Tudo o que você precisa para decidir com clareza.
              </h2>
              <p className="mt-4 text-slate-600">
                Seis recursos que colocam suas finanças no piloto automático — do dia a dia ao relatório para auditoria.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 90}>
                <Card className="group h-full border-slate-200/80 bg-white/70 backdrop-blur transition hover:-translate-y-1 hover:border-[#10b981]/50 hover:shadow-xl hover:shadow-[#10b981]/10">
                  <CardContent className="p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#0a1f44] to-[#102a5c] text-xl shadow-md">
                      <span aria-hidden>{f.emoji}</span>
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-[#0a1f44]">{f.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ---------- Dor vs. Solução ---------- */}
        <section id="solucao" className="px-4 pb-24 sm:px-6">
          <Reveal>
            <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-gradient-to-br from-[#0a1f44] via-[#0d2654] to-[#0a1f44] p-8 text-white shadow-2xl sm:p-14">
              <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#10b981]/30 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#10b981]/10 blur-3xl" />

              <div className="relative max-w-3xl">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#10b981]/40 bg-[#10b981]/10 px-3 py-1 text-xs font-semibold text-[#86efac]">
                  Da dor à solução
                </span>
                <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
                  Chega de planilhas complexas ou anotações perdidas.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-200 sm:text-lg">
                  O <span className="font-semibold text-white">Meu Controle</span> centraliza
                  sua vida financeira para você focar no que realmente importa: decidir com
                  clareza, gastar com tranquilidade e crescer com consistência.
                </p>
                <p className="mt-3 text-sm text-slate-300">
                  Ainda em dúvida?{" "}
                  <PrefetchLink
                    to="/blog/planilha-vs-aplicativo-controle-financeiro"
                    className="font-semibold text-[#10b981] underline-offset-4 hover:underline"
                  >
                    Compare planilha de gastos e aplicativo de controle financeiro
                  </PrefetchLink>
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {[
                    "Painel único: transações, cartões, contas a pagar e a receber e investimentos.",
                    "Cobranças de clientes com Pix, boleto e alerta no dia do vencimento.",
                    "Aportes e rendimentos separados no seu patrimônio.",
                    "Exportação em PDF e CSV pronta para o seu contador.",
                    "Funciona como PWA no celular, tablet e desktop.",
                    "Seus dados criptografados e isolados por conta.",
                  ].map((t) => (
                    <div key={t} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#10b981]" />
                      <span className="text-sm text-slate-200">{t}</span>
                    </div>
                  ))}
                </div>

                <Button
                  size="lg"
                  onClick={() => navigate("/auth")}
                  className="mt-9 h-12 gap-2 bg-[#10b981] px-7 text-base font-semibold text-white shadow-lg shadow-[#10b981]/40 hover:bg-[#0ea371]"
                >
                  Começar agora — é grátis
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ---------- Instale como app ---------- */}
        <section id="instalar" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-[#0a1f44]/10 bg-gradient-to-br from-[#0a1f44] via-[#0d2a5c] to-[#047857] p-8 text-white shadow-2xl shadow-[#0a1f44]/30 sm:p-12">
              <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#10b981]/25 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

              <div className="relative grid items-center gap-10 lg:grid-cols-2">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold">
                    <Smartphone className="h-3.5 w-3.5" />
                    Sem loja de aplicativos · Sem APK
                  </div>
                  <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl">
                    Instale o Meu Controle como app no seu celular
                  </h2>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-white/80 sm:text-base">
                    Funciona como um app nativo: ícone na tela inicial, abre em tela cheia e
                    carrega instantaneamente. Instalação oficial e segura, verificada pelo
                    próprio navegador — nada de arquivos baixados de terceiros.
                  </p>
                  <div className="mt-6">
                    <InstallAppButton variant="hero" />
                  </div>
                  <p className="mt-4 flex items-center gap-2 text-xs text-white/70">
                    <ShieldCheck className="h-4 w-4 text-[#34d399]" />
                    Conexão criptografada (HTTPS) e instalação validada pelo Chrome/Safari
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#34d399]/20">
                        <Download className="h-4 w-4 text-[#34d399]" />
                      </span>
                      <h3 className="text-sm font-bold">Android / Computador</h3>
                    </div>
                    <ol className="mt-3 space-y-2 text-xs leading-relaxed text-white/80">
                      <li><strong className="text-white">1.</strong> Toque em <strong className="text-white">"Instalar App"</strong></li>
                      <li><strong className="text-white">2.</strong> Confirme no aviso do navegador</li>
                      <li><strong className="text-white">3.</strong> Pronto — ícone na tela inicial</li>
                    </ol>
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#34d399]/20">
                        <Share className="h-4 w-4 text-[#34d399]" />
                      </span>
                      <h3 className="text-sm font-bold">iPhone / iPad</h3>
                    </div>
                    <ol className="mt-3 space-y-2 text-xs leading-relaxed text-white/80">
                      <li><strong className="text-white">1.</strong> Toque em <strong className="text-white">"Instalar App"</strong> (abre o menu Compartilhar)</li>
                      <li><strong className="text-white">2.</strong> Escolha <strong className="text-white">"Adicionar à Tela de Início"</strong> <Plus className="inline h-3 w-3" /></li>
                      <li><strong className="text-white">3.</strong> Toque em <strong className="text-white">"Adicionar"</strong></li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ---------- FAQ ---------- */}
        <section id="faq" className="mx-auto max-w-4xl px-4 pb-24 sm:px-6">
          <Reveal>
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#047857]">
                Perguntas frequentes
              </span>
              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a1f44] sm:text-4xl">
                Dúvidas sobre o app de controle financeiro Meu Controle
              </h2>
              <p className="mt-4 text-slate-600">
                Respostas rápidas sobre fatura real de cartão de crédito, alertas de contas a pagar,
                orçamento mensal, teste grátis e pagamento da assinatura.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 space-y-10">
            {faqGroups.map((group, gi) => (
              <Reveal key={group.title} delay={gi * 60}>
                <div>
                  <h3 className="mb-4 px-1 text-sm font-bold uppercase tracking-[0.14em] text-[#0a1f44]">
                    {group.title}
                  </h3>
                  <div className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    {group.items.map((f) => (
                      <details key={f.q} className="group">
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-5 text-left transition hover:bg-slate-50 sm:px-6">
                          <h4 className="text-base font-semibold text-[#0a1f44] sm:text-lg">
                            {f.q}
                          </h4>
                          <span
                            aria-hidden
                            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 text-[#047857] transition group-open:rotate-45 group-open:border-[#10b981] group-open:bg-[#10b981]/10"
                          >
                            +
                          </span>
                        </summary>
                        <div className="px-5 pb-5 text-sm leading-relaxed text-slate-600 sm:px-6 sm:text-[15px]">
                          {f.a}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          {/* FAQPage JSON-LD é injetado no <head> pelo componente <Seo /> */}

        </section>
      </main>

      {/* ---------- Footer ---------- */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-3">
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0a1f44] to-[#10b981]">
                <Wallet className="h-5 w-5 text-white" />
              </div>
              <span className="text-base font-bold text-[#0a1f44]">Meu Controle</span>
            </Link>
            <p className="mt-4 max-w-xs text-sm text-slate-600">
              Sua vida financeira centralizada, com clareza e segurança.
            </p>
          </div>

          <div>
            <p className="footer-title text-sm font-semibold text-[#0a1f44]">Produto</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li><a href="#funcionalidades" className="hover:text-[#0a1f44]">Funcionalidades</a></li>
              <li><a href="#solucao" className="hover:text-[#0a1f44]">Por que usar</a></li>
              <li><PrefetchLink to="/blog" className="hover:text-[#0a1f44]">Blog de finanças</PrefetchLink></li>
              <li><Link to="/auth" className="hover:text-[#0a1f44]">Criar conta</Link></li>
            </ul>
          </div>

          <div>
            <p className="footer-title text-sm font-semibold text-[#0a1f44]">Institucional</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li><Link to="/privacidade" className="hover:text-[#0a1f44]">Política de privacidade</Link></li>
              <li><Link to="/termos" className="hover:text-[#0a1f44]">Termos de uso</Link></li>
              <li><Link to="/sobre" className="hover:text-[#0a1f44]">Sobre nós</Link></li>
              <li>
                <a
                  href="https://www.instagram.com/fluxsistemas"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#0a1f44]"
                >
                  Contato
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-slate-500 sm:flex-row sm:px-6">
            <p>© {new Date().getFullYear()} Meu Controle. Todos os direitos reservados.</p>
            <a
              href="https://www.instagram.com/fluxsistemas"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1.5 font-semibold"
            >
              <Instagram className="h-3.5 w-3.5 text-pink-500 transition group-hover:scale-110" />
              <span className="bg-gradient-to-r from-fuchsia-500 via-pink-500 to-amber-400 bg-clip-text text-transparent">
                Desenvolvido por FS.FluxSistemas
              </span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
